import * as config from "../constants/config";
import * as priceTypes from "../constants/priceTypes";
import * as conversionHelper from "../helpers/conversionHelper";

/**
 * Validation of decimal value during typing value into input field.
 *
 * @param   {String}    value
 * @returns {Boolean}
 */
export const isDecimalValueValid = (val) => {
    if (typeof val !== "string") {
        return false;
    }

    let value = val.replace(",", ".").trim();

    if (value.length === 0) {
        return true;
    }

    if (value.startsWith("-")) {
        return false;
    }

    if (value.includes(".")) {
        if (value === ".") {
            return false;
        }

        if (value.indexOf(".") > 10) {
            return false;
        }

        if (value.endsWith(".")) {
            let substring = value.substr(0, value.length - 1);

            if (substring.includes(".")) {
                return false;
            }

            return substring.match(/^-?\d*(\.\d+)?$/) !== null;
        }

        if (value.length > 16) {
            return false;
        }

        return value.match(/^-?\d*(\.\d+)?$/) !== null;
    }

    if (value.length > 10) {
        return false;
    }

    return value.match(/^-?\d*(\.\d+)?$/) !== null;
};

export const isCurrencyCodeValid = (value) => {
    if (value.length === 0) {
        return true;
    }

    let currencyCodes = require("currency-codes");

    if (currencyCodes.codes().includes(value.toUpperCase())) {
        return true;
    }

    return false;
};

/**
 * Validation of integer value during typing value into input field.
 *
 * @param   {String}    value
 * @returns {Boolean}
 */
export const isIntegerValueValid = (value) => {
    if (value === "" || typeof value !== "string") {
        return true;
    }

    return value.match(/^\d*$/) !== null;
};

export const isPercentValueValid = (value) => {
    if (value === null || (value >= 0 && value <= 100)) {
        return true;
    }

    return false;
};

export const isLabLValueValid = (labL) => {
    return labL >= 0 && labL <= 100;
};

export const isLabAValueValid = (labA) => {
    return labA >= -128 && labA <= 127;
};

export const isLabBValueValid = (labB) => {
    return labB >= -128 && labB <= 127;
};

export const isLabValueValid = (lab) => {
    if (isLabLValueValid(lab.labL) && isLabAValueValid(lab.labA) && isLabBValueValid(lab.labB)) {
        return true;
    }
    return false;
};

export const isXyzXValueValid = (xyzX) => {
    return xyzX >= 0 && xyzX <= 95.047;
};
export const isXyzYValueValid = (xyzY) => {
    return xyzY >= 0 && xyzY <= 100;
};
export const isXyzZValueValid = (xyzZ) => {
    return xyzZ >= 0 && xyzZ <= 108.883;
};

export const isXyzValueValid = (xyz) => {
    if (isXyzXValueValid(xyz.xyzX) && isXyzYValueValid(xyz.xyzY) && isXyzZValueValid(xyz.xyzZ)) {
        return true;
    }
    return false;
};

export const isUfiThresholdValid = (threshold) => {
    return threshold >= 0.05 && threshold <= 0.99;
};

/**
 * Format a decimal value
 *
 * @param   {String}    value
 * @returns {String}
 */
export const formatDecimal = (value) => {
    if (typeof value === "string") {
        return value.replace(",", ".");
    }

    return String(value);
};

/**
 * Checks whether string is JSON object
 *
 * @param String
 * @returns {Boolean}
 */
export const isJSON = (string) => {
    try {
        JSON.parse(string);
    } catch (e) {
        return false;
    }

    return true;
};

/**
 * Checks whether any colorant*canTotal is greater than amount of the pigment in the canister
 */
export const isOrderGreaterThanResources = (tabId, state$) => {
    if (
        state$.value.formula[tabId].formula.realColorantList.find((colorant) => {
            return (
                conversionHelper.convertAmountByColorant(
                    colorant,
                    colorant.amountType,
                    state$.value.rcc.machineConfigurationList.unit
                ) *
                    state$.value.formula[tabId].numCanTotal >
                state$.value.rcc.canisterList.find((canister) => {
                    return colorant.id === canister.colorant?.id && canister.currentLevel < canister.capacity;
                })?.currentLevel
            );
        })
    ) {
        return true;
    }
    return false;
};

/**
 * Parses given value to boolean
 *
 * @param value {String|Boolean}
 * @returns {Boolean}
 */
export const parseBooleanValue = (value) => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        if (value.toLowerCase() === "true") {
            return true;
        }

        return false;
    }

    return false;
};

export const isHostValid = (ipAddress) => {
    if (ipAddress === config.LOCALHOST) {
        return true;
    }

    if (
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
            ipAddress
        )
    ) {
        return true;
    }

    return false;
};

export const isPortValid = (port) => {
    if (port < 0 || port > 65535) {
        return false;
    }

    return true;
};

export const isWebSocketUrlValid = (rawUrl) => {
    if (rawUrl === "" || rawUrl.indexOf(config.WEBSOCKET_PROTOCOL) !== 0) {
        return false;
    }

    let url = rawUrl.substring(5);

    if (url.indexOf(":") === -1) {
        return false;
    }

    let urlParts = url.split(":");

    if (urlParts.length !== 2) {
        return false;
    }

    if (urlParts[0] === "" || urlParts[1] === "") {
        return false;
    }

    if (!isPortValid(Number(urlParts[1]))) {
        return false;
    }

    return isHostValid(urlParts[0]);
};

/**
 * Format UTC time for server
 *
 * @param time {Date}
 * @returns {String}
 */
export const formatTimeForServer = (time) => {
    if (time === null) {
        return "";
    }

    return time.getFullYear() + "-" + (time.getMonth() + 1) + "-" + time.getDate();
};

export const isItemValid = (list, itemName) => {
    for (let item of list) {
        if (item.name === itemName) {
            return true;
        }
    }

    return false;
};

export const getValidItem = (list, itemName) => {
    if (list.length === 0) {
        return null;
    }

    if (isItemValid(list, itemName)) {
        return itemName;
    }

    return list[0].name;
};

export const getPrinter = (printerList, printerName) => {
    if (isItemValid(printerList, printerName)) {
        return printerName;
    }

    return null;
};

export const getPrinterSafe = (printerList, printerName, defaultPrinter) => {
    if (isItemValid(printerList, printerName)) {
        return printerName;
    }

    if (isItemValid(printerList, defaultPrinter)) {
        return defaultPrinter;
    }

    return null;
};

export const getTemplate = (list, itemName, defaultItem) => {
    if (list.length === 0) {
        return null;
    }

    if (isItemValid(list, itemName)) {
        return itemName;
    }

    if (isItemValid(list, defaultItem)) {
        return defaultItem;
    }

    return list[0].name;
};

export const mergeWeberSettings = (state, settings) => {
    return {
        weberCanBigLowerBound:
            state.weberCanBigLowerBound && !isNaN(state.weberCanBigLowerBound)
                ? state.weberCanBigLowerBound
                : settings.weberCanBigLowerBound,
        weberCanBigUpperBound:
            state.weberCanBigUpperBound && !isNaN(state.weberCanBigUpperBound)
                ? state.weberCanBigUpperBound
                : settings.weberCanBigUpperBound,
        weberCanCoatingLowerBound:
            state.weberCanCoatingLowerBound && !isNaN(state.weberCanCoatingLowerBound)
                ? state.weberCanCoatingLowerBound
                : settings.weberCanCoatingLowerBound,
        weberCanCoatingUpperBound:
            state.weberCanCoatingUpperBound && !isNaN(state.weberCanCoatingUpperBound)
                ? state.weberCanCoatingUpperBound
                : settings.weberCanCoatingUpperBound,
        weberCanLaboratoryLowerBound:
            state.weberCanLaboratoryLowerBound && !isNaN(state.weberCanLaboratoryLowerBound)
                ? state.weberCanLaboratoryLowerBound
                : settings.weberCanLaboratoryLowerBound,
        weberCanLaboratoryUpperBound:
            state.weberCanLaboratoryUpperBound && !isNaN(state.weberCanLaboratoryUpperBound)
                ? state.weberCanLaboratoryUpperBound
                : settings.weberCanLaboratoryUpperBound,
        weberCanSmallLowerBound:
            state.weberCanSmallLowerBound && !isNaN(state.weberCanSmallLowerBound)
                ? state.weberCanSmallLowerBound
                : settings.weberCanSmallLowerBound,
        weberCanSmallUpperBound:
            state.weberCanSmallUpperBound && !isNaN(state.weberCanSmallUpperBound)
                ? state.weberCanSmallUpperBound
                : settings.weberCanSmallUpperBound,
        weberCoatingNames:
            state.weberCoatingNames && Array.isArray(state.weberCoatingNames)
                ? state.weberCoatingNames
                : settings.weberCoatingNames,
        weberPlasterNames:
            state.weberPlasterNames && Array.isArray(state.weberPlasterNames)
                ? state.weberPlasterNames
                : settings.weberPlasterNames
    };
};

export const isPhoneValid = (phoneNumber) => {
    if (typeof phoneNumber !== "string" || phoneNumber === "") {
        return true;
    }

    if (phoneNumber.match(/^([0,1,2,3,4,5,6,7,8,9,(,),+]+)$/)) {
        return true;
    }

    return false;
};

export const isEmailValid = (email) => {
    if (typeof email !== "string" || email === "") {
        return true;
    }

    if (email.match(/\S+@\S+\.\S+/)) {
        return true;
    }

    return false;
};

export const isUsernameValid = (username) => {
    if (typeof username === "string" && username.match(/^[a-zA-Z0-9_-]{0,16}$/)) {
        return true;
    }

    return false;
};

export const isUrlValid = (url) => {
    if (
        typeof url === "string" &&
        url.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g)
    ) {
        return true;
    }

    return false;
};

export const isNoteValid = (note) => {
    if (note === "" || (typeof note === "string" && note.match(/^[a-zA-Z0-9_-].*/g))) {
        return true;
    }

    return false;
};

export const isValidColor = (hexCode) => {
    if (typeof hexCode === "string" && hexCode.match(/^#[0-9A-Fa-f]{6}$/)) {
        return true;
    }

    return false;
};

export const isWeberNoteValid = (note) => {
    if (typeof note === "string") {
        if (note !== config.WEBER_NO_ORDER_NOTE && isNoteValid(note) && note !== "") {
            return true;
        }
    }

    return false;
};

const isMarginValueValid = (value) => {
    if (value === null || (value >= 0 && value < 100)) {
        return true;
    }

    return false;
};

export const isMarginMarkupDiscountValid = (type, value) => {
    if (!isDecimalValueValid(value)) {
        return false;
    }

    switch (type) {
        case priceTypes.MARGIN_TYPE_MARGIN:
            return isMarginValueValid(Number(value));
        case priceTypes.MARGIN_TYPE_MARKUP:
            return true;
        case priceTypes.MARGIN_TYPE_DISCOUNT:
            return isPercentValueValid(Number(value));
        default:
            return true;
    }
};

const UFI_CODE_CHARACTER_SET = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    A: 10,
    C: 11,
    D: 12,
    E: 13,
    F: 14,
    G: 15,
    H: 16,
    J: 17,
    K: 18,
    M: 19,
    N: 20,
    P: 21,
    Q: 22,
    R: 23,
    S: 24,
    T: 25,
    U: 26,
    V: 27,
    W: 28,
    X: 29,
    Y: 30
};

export const isUfiCodeValid = (text) => {
    if (typeof text !== "string") {
        return false;
    }

    let ufi = text.trim();

    if (ufi.length !== 19) {
        return false;
    }

    if (ufi.match(/^(([A-Y0-9]{4})-([A-Y0-9]{4})-([A-Y0-9]{4})-([A-Y0-9]{4}))$/) === null) {
        return false;
    }

    let splitted = ufi.replace(/-/g, "").split("");

    if (splitted.some((ufiChar) => UFI_CODE_CHARACTER_SET[ufiChar] === undefined)) {
        return false;
    }

    let sum = 0;

    for (let [i, ch] of splitted.entries()) {
        if (i > 0) {
            sum += (i + 1) * UFI_CODE_CHARACTER_SET[ch];
        }
    }

    let checksum = (31 - (sum % 31)) % 31;

    for (let [zita, lucka] of Object.entries(UFI_CODE_CHARACTER_SET)) {
        if (checksum === lucka) {
            return zita === splitted[0];
        }
    }

    return false;
};

export const countProps = (obj) => {
    let count = 0;
    for (const k in obj) {
        if (obj.hasOwnProperty(k)) {
            count++;
        }
    }
    return count;
};

export const isEqual = (v1, v2) => {
    if (typeof v1 !== typeof v2) {
        return false;
    }

    if (typeof v1 === "function") {
        return v1.toString() === v2.toString();
    }

    if (v1 instanceof Object && v2 instanceof Object) {
        if (countProps(v1) !== countProps(v2)) {
            return false;
        }
        let r = true;
        for (const k in v1) {
            r = isEqual(v1[k], v2[k]);
            if (!r) {
                return false;
            }
        }
        return true;
    } else {
        return v1 === v2;
    }
};

export const isString = (value) => {
    return value instanceof String || typeof value === "string";
};

export const isListChanged = (prevList, list) => {
    if (prevList.length !== list.length) {
        return true;
    }

    return !list.every((item) => prevList.includes(item));
};
